'use client';
import debounce from 'debounce';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { VisibilityGuardWrapper } from './styles';

type Props = {
	slotContent: React.ReactNode;
	parallelRoutePathPrefix: string;
	children: React.ReactNode;
};

type ScrollRestorationOptions = {
	isPageVisible: boolean;
	parallelRoutePathPrefix: string;
};

const useScrollRestoration = ({
	isPageVisible,
	parallelRoutePathPrefix,
}: ScrollRestorationOptions) => {
	const savedScrollY = useRef(0);

	useEffect(() => {
		if (!isPageVisible) return;

		const saveScrollPosition = debounce(() => {
			// In some cases scroll event is called after navigation
			if (!location.pathname.startsWith(parallelRoutePathPrefix)) {
				savedScrollY.current = window.scrollY;
			}
		}, 40);

		window.addEventListener('scroll', saveScrollPosition);
		return () => {
			window.removeEventListener('scroll', saveScrollPosition);
		};
	}, [isPageVisible, parallelRoutePathPrefix]);

	// Restore the scroll position before paint to avoid flicker
	useLayoutEffect(() => {
		if (isPageVisible && savedScrollY.current > 0) {
			window.scrollTo({ top: savedScrollY.current });
		}

		if (!isPageVisible) {
			window.scrollTo({ top: 0 });
			// Fix scroll position on iOS
			requestAnimationFrame(() => {
				window.scrollTo({ top: 0 });
			});
		}
	}, [isPageVisible]);
};

/**
 * Guard to temporarily hide main page content
 */
export const VisibilityGuard = ({
	slotContent,
	parallelRoutePathPrefix,
	children,
}: Props) => {
	const isPageVisible = !slotContent;

	useScrollRestoration({ isPageVisible, parallelRoutePathPrefix });

	return (
		<>
			<VisibilityGuardWrapper isVisible={isPageVisible}>
				{children}
			</VisibilityGuardWrapper>
			{!isPageVisible && slotContent}
		</>
	);
};
